import { useEffect, useMemo, useRef, useState } from "react";
import "./InstallPWA.css";

const DEFAULT_PORTAL_NAME = "Portal Pacientes";

const getPortalName = () => {
  const configuredName =
    localStorage.getItem("tituloWebPortal") ||
    localStorage.getItem("tituloWeb") ||
    localStorage.getItem("nombreClinica") ||
    document.title;

  return (configuredName || DEFAULT_PORTAL_NAME).trim();
};

const getPortalStorageKey = (portalName) => {
  const safePortalName = portalName.replace(/[^a-z0-9]+/gi, "");
  return `iosModalDismissed${safePortalName || "PortalPacientes"}`;
};

const isStandaloneMode = () =>
  window.navigator.standalone === true ||
  window.matchMedia("(display-mode: standalone)").matches;

const InstallPWA = () => {
  const [showModal, setShowModal] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [platform, setPlatform] = useState("other");
  const [iosBrowser, setIosBrowser] = useState("");
  const [portalName, setPortalName] = useState(DEFAULT_PORTAL_NAME);
  const [installed, setInstalled] = useState(false);
  const deferredPromptRef = useRef(null);

  const iosDismissKey = useMemo(
    () => getPortalStorageKey(portalName),
    [portalName]
  );

  useEffect(() => {
    const resolvedPortalName = getPortalName();
    const userAgent = navigator.userAgent || "";
    const iosDevice =
      /iPad|iPhone|iPod/.test(userAgent) ||
      (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
    const androidDevice = /Android/i.test(userAgent);
    const standalone = isStandaloneMode();
    const appleTitleMeta = document.querySelector(
      'meta[name="apple-mobile-web-app-title"]'
    );

    setPortalName(resolvedPortalName);
    setInstalled(standalone);

    if (appleTitleMeta) {
      appleTitleMeta.setAttribute("content", resolvedPortalName);
    }

    if (standalone) {
      return undefined;
    }

    if (iosDevice) {
      setPlatform("ios");
      setIosBrowser(/CriOS/i.test(userAgent) ? "Chrome" : "Safari");

      if (!localStorage.getItem(getPortalStorageKey(resolvedPortalName))) {
        const iosTimer = window.setTimeout(() => {
          setShowModal(true);
        }, 2000);

        return () => {
          window.clearTimeout(iosTimer);
        };
      }

      return undefined;
    }

    if (androidDevice) {
      setPlatform("android");
    } else {
      setPlatform("desktop");
    }

    const handleBeforeInstallPrompt = (event) => {
      event.preventDefault();
      deferredPromptRef.current = event;
      setDeferredPrompt(event);
      setShowModal(true);
    };

    const handleInstalled = () => {
      deferredPromptRef.current = null;
      setDeferredPrompt(null);
      setInstalled(true);
      setShowModal(false);
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    window.addEventListener("appinstalled", handleInstalled);

    let androidFallbackTimer;
    if (androidDevice) {
      androidFallbackTimer = window.setTimeout(() => {
        if (!deferredPromptRef.current && !isStandaloneMode()) {
          setShowModal(true);
        }
      }, 2000);
    }

    return () => {
      window.removeEventListener(
        "beforeinstallprompt",
        handleBeforeInstallPrompt
      );
      window.removeEventListener("appinstalled", handleInstalled);
      if (androidFallbackTimer) {
        window.clearTimeout(androidFallbackTimer);
      }
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPromptRef.current) {
      return;
    }

    deferredPromptRef.current.prompt();
    const { outcome } = await deferredPromptRef.current.userChoice;

    if (outcome !== "accepted") {
      setShowModal(false);
      return;
    }

    deferredPromptRef.current = null;
    setDeferredPrompt(null);
    setShowModal(false);
  };

  const handleClose = () => {
    setShowModal(false);

    if (platform === "ios") {
      localStorage.setItem(iosDismissKey, "true");
    }
  };

  if (installed || !showModal) {
    return null;
  }

  const showInstallButton = platform !== "ios" && Boolean(deferredPrompt);

  return (
    <div className="install-pwa-overlay" role="presentation">
      <div
        className="install-pwa-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="install-pwa-title"
      >
        <h2 id="install-pwa-title">
          {showInstallButton
            ? "Crear acceso directo"
            : "Crear acceso directo al portal"}
        </h2>

        {platform === "ios" ? (
          <div className="install-pwa-content">
            <p>
              Para crear el acceso directo de <strong>{portalName}</strong> en
              tu iPhone:
            </p>
            <ol>
              <li>
                Toca <strong>Compartir</strong> en {iosBrowser || "Safari"}.
              </li>
              <li>
                Elige <strong>Agregar a pantalla de inicio</strong>.
              </li>
              <li>
                Toca <strong>Crear</strong> para confirmar.
              </li>
            </ol>
          </div>
        ) : platform === "android" && !deferredPrompt ? (
          <div className="install-pwa-content">
            <p>
              Si el navegador no muestra el instalador automático, podés crear
              el acceso directo manualmente:
            </p>
            <ol>
              <li>
                Abrí el menú de <strong>tres puntos</strong>.
              </li>
              <li>
                Elegí <strong>Instalar app</strong> o{" "}
                <strong>Agregar a pantalla principal</strong>.
              </li>
              <li>
                Confirmá la acción para crear el acceso directo de{" "}
                <strong>{portalName}</strong>.
              </li>
            </ol>
          </div>
        ) : (
          <div className="install-pwa-content">
            <p>
              Creá el acceso directo de <strong>{portalName}</strong> para
              entrar más rápido desde tu dispositivo.
            </p>
          </div>
        )}

        <div className="install-pwa-actions">
          {showInstallButton ? (
            <button
              type="button"
              className="install-pwa-button install-pwa-button-primary"
              onClick={handleInstall}
            >
              Instalar
            </button>
          ) : null}
          <button
            type="button"
            className="install-pwa-button install-pwa-button-secondary"
            onClick={handleClose}
          >
            {platform === "ios" ? "Entendido" : "Cerrar"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default InstallPWA;
